import { useState } from "react";
import { ERROR_MESSAGES } from "../../constant/constant";

/**
 * @typedef {Object} DummyStudent
 * @property {number} id
 * @property {string} name
 * @property {string} username
 */

/**
 * @typedef {Object} SubscriptionData
 * @property {boolean} is_subscribed
 * @property {DummyStudent} [dummy_student]
 */

/**
 * @typedef {Object} SocialLoginUser
 * @property {string} firstName
 * @property {string|null} lastName
 * @property {string} email
 * @property {number} role_id
 * @property {string} status
 * @property {number} id
 * @property {number} role
 * @property {string} role_slug
 * @property {string|null} parent_type
 * @property {SubscriptionData} subscription_data
 */

/**
 * @typedef {Object} SocialLoginResponse
 * @property {boolean} tenant
 * @property {string|number|null} tenant_id
 * @property {string} token
 * @property {string} refresh_token
 * @property {number} access_token_expiry
 * @property {boolean} is_new_user
 * @property {SocialLoginUser} user
 */

/**
 * Generic hook to authenticate a user via a social OAuth access token.
 * Sends the access_token to the backend for verification and login.
 *
 * @param {"google" | "facebook"} [provider="google"] - The social login provider
 */
export const useSocialLoginAuth = (provider = "google") => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;

  /**
   * Sends the social access token to the backend for authentication
   * @param {string} accessToken - The OAuth access token
   * @param {(data: SocialLoginResponse) => void} [onSuccess] - Callback on success
   */
  const socialLogin = async (accessToken, onSuccess) => {
    setLoading(true);
    setError("");
    setData(null);

    try {
      const response = await fetch(
        `${apiUrl}/auth/${provider}/token-login?access_token=${encodeURIComponent(accessToken)}`,
        {
          headers: { Accept: "application/json" },
        },
      );

      if (!response.ok) {
        let errorMsg = ERROR_MESSAGES?.UNKNOWN || "Something went wrong";
        try {
          const errorData = await response.json();
          if (errorData?.errors) {
            const errorKey = Object.keys(errorData.errors)[0];
            errorMsg = errorData.errors[errorKey][0];
          } else {
            errorMsg = errorData?.message || errorMsg;
          }
        } catch {
          if (response.status === 500) {
            errorMsg =
              ERROR_MESSAGES?.INTERNAL_SERVER || "Internal server error";
          }
        }
        throw new Error(errorMsg);
      }
      /** @type {SocialLoginResponse} */
      const responseData = await response.json();
      setData(responseData);

      if (onSuccess) {
        onSuccess(responseData);
      }

      return responseData;
    } catch (err) {
      setError(
        err?.message || ERROR_MESSAGES?.UNKNOWN || "Something went wrong",
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    socialLogin,
    googleLogin: socialLogin, // backward-compatible alias
    loading,
    error,
    data,
    setError,
  };
};

/**
 * Backward-compatible wrapper — calls useSocialLoginAuth with "google".
 */
export const useGoogleLoginAuth = () => useSocialLoginAuth("google");
